import { CommentWithRelations } from "@acme/types";
import { Avatar, AvatarFallback, AvatarImage } from "@acme/ui/avatar";
import { Skeleton } from "@acme/ui/skeleton";
import { JSONContent } from "@tiptap/react";
import { useMemo, useState } from "react";
import Timestamp from "~/components/timestamp";
import { CommentContext, CommentProvider } from "~/contexts/comment";
import CommentActions from "./comment-actions";
import CommentEditor, { useCommentEditor } from "./comment-editor";
import CommentMenu from "./comment-menu";
import CommentReplies from "./comment-replies";
import CommentReply from "./comment-reply";

interface CommentItemProps {
  articleSlug: string;
  comment: CommentWithRelations;
}

export default function CommentItem({
  comment,
  articleSlug,
}: Readonly<CommentItemProps>) {
  const {
    user,
    comment: { id, parentId, content, createdAt },
    repliesCount,
  } = comment;

  const [editor, setEditor] = useCommentEditor();

  const [isReplying, setIsReplying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isOpenReplies, setIsOpenReplies] = useState(false);

  const context = useMemo<CommentContext>(
    () => ({
      isEditing,
      isReplying,
      isOpenReplies,
      setIsEditing,
      setIsReplying,
      setIsOpenReplies,
      comment,
    }),
    [comment, isEditing, isOpenReplies, isReplying]
  );

  return (
    <CommentProvider value={context}>
      <div
        className="overflow-hidden"
        id={parentId ? `comment-${parentId}-${id}` : `comment-${id}`}
      >
        <div className="flex gap-2 p-2 sm:px-4">
          {user?.image ? (
            <Avatar className="size-8">
              <AvatarImage src={user.image} />
              <AvatarFallback className="bg-transparent">
                <Skeleton className="size-8 rounded-full" />
              </AvatarFallback>
            </Avatar>
          ) : null}

          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <div className="font-semibold">{user?.name}</div>

                <Timestamp datetime={createdAt.toString()} />
              </div>

              <CommentMenu comment={comment.comment} />
            </div>

            <CommentEditor
              content={content as JSONContent}
              editable={false}
              editor={editor}
              onChange={setEditor}
            />

            {isReplying ? <CommentReply /> : <CommentActions />}

            {!parentId && repliesCount > 0 ? (
              <CommentReplies articleSlug={articleSlug} />
            ) : null}
          </div>
        </div>
      </div>
    </CommentProvider>
  );
}
